import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { cx } from 'class-variance-authority';
import { useRef } from 'react';

import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

import type { PopAccuracyComfortSectionProps } from './PopAccuracyComfortSection';
import PopAccuracyComfortSectionHighlights from './PopAccuracyComfortSectionHighlights';
import type { PopAccuracyComfortSectionVideoCtaProps } from './PopAccuracyComfortSectionVideoCta';
import PopAccuracyComfortSectionVideoCta from './PopAccuracyComfortSectionVideoCta';

export interface PopAccuracyComfortSectionContentProps
  extends Pick<
    PopAccuracyComfortSectionProps,
    | 'contentIntroTransitionDelay'
    | 'highlights'
    | 'index'
    | 'title'
    | 'titleClassName'
  > {
  readonly isSectionVisible: boolean;
  readonly videoCta: Pick<
    PopAccuracyComfortSectionVideoCtaProps,
    'label' | 'onClick'
  >;
}

const PopAccuracyComfortSectionContent = ({
  contentIntroTransitionDelay = {
    sm: 0.25,
    md: 0.75,
  },
  highlights,
  isSectionVisible,
  title,
  titleClassName,
  videoCta,
}: PopAccuracyComfortSectionContentProps) => {
  const contentMotionRef = useRef<HTMLDivElement>(null);
  const isMediumScreen = useMediaQuery(`(min-width: ${BREAKPOINTS.medium}px)`);
  const introTransitionDelay =
    contentIntroTransitionDelay[isMediumScreen ? 'md' : 'sm'];

  return (
    <div
      className="relative z-20 pb-14 motion-safe:mt-[-25vh] motion-reduce:mt-[-25vw] md:mt-0 md:h-full md:pt-24 md:pb-40 lg:pb-24"
      data-cy="pop-accuracy-comfort-section-content"
    >
      <LayoutGrid className="md:h-full">
        <GridItem
          colEnd={{ sm: 'main', md: 12, lg: 10, xl: 9 }}
          colStart={{ sm: 'main' }}
        >
          <Motion
            animate={{ opacity: isSectionVisible ? 1 : 0 }}
            className="md:flex md:h-full md:flex-col"
            initial={{ opacity: 0 }}
            ref={contentMotionRef}
            transition={{
              delay: isSectionVisible ? introTransitionDelay : 0,
              duration: 0.75,
              type: 'tween',
            }}
          >
            <SectionHeading
              className={cx('inline w-screen', titleClassName)}
              content={title}
              typographyProps={{
                className: 'mb-8 leading-tighter text-current font-normal',
                Element: 'h2',
                variant: 'h3',
              }}
            />
            <PopAccuracyComfortSectionVideoCta
              {...videoCta}
              introTransitionDelay={introTransitionDelay}
              isSectionVisible={isSectionVisible}
            />
            <PopAccuracyComfortSectionHighlights highlights={highlights} />
          </Motion>
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default PopAccuracyComfortSectionContent;
